import { useAuth } from "@clerk/tanstack-react-start";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { ArrowLeft, Send } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { AuthModal } from "~/components/auth-modal";
import { CoachSiteChrome } from "~/components/layouts/coach-site-chrome";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { shouldShowMessagingCTAs } from "~/lib/coach-cta-utils";
import { startConversationWithCoach } from "~/lib/functions/messaging";
import { trackMessageSent } from "~/lib/monitoring/track-event";
import { coachStatusQueryOptions } from "~/lib/queries/coachStatusQuery";
import { coachQueryOptions } from "~/lib/query-options";
import { createMetaTags } from "~/lib/shared/meta";

const contactFormSchema = z.object({
  subject: z
    .string()
    .min(1, "Subject is required")
    .max(200, "Subject must be 200 characters or less"),
  content: z
    .string()
    .min(1, "Message is required")
    .max(5000, "Message must be 5000 characters or less"),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

export const Route = createFileRoute("/$username/contact/")({
  loader: async ({ context, params }) => {
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(params.username),
    );

    if (!coach) {
      throw new Error("Coach not found");
    }

    // Refuse in the loader (runs server-side on direct hits) rather than only
    // hiding CTAs: pre-generated (unclaimed) profiles have no account behind
    // them, and booking-mode coaches don't accept messages.
    if (!shouldShowMessagingCTAs(coach)) {
      throw redirect({
        to: "/$username",
        params: { username: coach.username },
      });
    }

    return { coach };
  },
  // Always noindex,follow (#759): the contact form is a thin duplicate of the
  // profile, but followed links keep equity flowing back to it.
  head: ({ loaderData, params }) => {
    const coachName = loaderData?.coach?.fullName ?? params.username;

    return createMetaTags({
      title: `Contact ${coachName} | Dream Coach Match`,
      description: `Send a message to ${coachName} on Dream Coach Match.`,
      noindexFollow: true,
    });
  },
  component: ContactPage,
});

function ContactPage() {
  const { coach } = Route.useLoaderData();
  const router = useRouter();
  const { isSignedIn, userId } = useAuth();
  // Shares its cache entry with the site header, so this costs no extra request.
  const { data: coachStatus } = useQuery(
    coachStatusQueryOptions(userId ?? undefined),
  );
  const isOwnPage = !!coachStatus?.coachId && coachStatus.coachId === coach.id;
  const [showAuthModal, setShowAuthModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      subject: "",
      content: "",
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: (data: ContactFormData) => {
      return startConversationWithCoach({
        data: {
          username: coach.username,
          subject: data.subject,
          content: data.content,
        },
      });
    },
    onSuccess: (result) => {
      trackMessageSent(coach.username);
      toast.success("Message sent");
      reset();
      router.navigate({
        to: "/messages/$conversationId",
        params: { conversationId: result.conversationId },
      });
    },
    onError: (error) => {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    },
  });

  const [pendingFormData, setPendingFormData] =
    useState<ContactFormData | null>(null);

  const onSubmit = (data: ContactFormData) => {
    if (!isSignedIn) {
      setPendingFormData(data);
      setShowAuthModal(true);
      return;
    }
    sendMessageMutation.mutate(data);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // The user is now authenticated, submit the pending form data
    if (pendingFormData) {
      // Add a small delay to ensure user creation is complete
      setTimeout(() => {
        sendMessageMutation.mutate(pendingFormData);
        setPendingFormData(null);
      }, 100);
    }
  };

  const coachFullName = `${coach.firstName} ${coach.lastName}`;

  return (
    <CoachSiteChrome>
      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="mb-6">
          <Link
            to="/$username"
            params={{ username: coach.username }}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to {coachFullName}'s Profile
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Send className="mr-2 h-5 w-5" />
              Contact {coachFullName}
            </CardTitle>
            <p className="text-sm text-gray-600">
              Send a message to {coach.firstName} and they'll receive an email
              notification.{" "}
              {!isSignedIn && "You'll need to create an account first, then "}
              You'll be able to continue the conversation in your messages
              dashboard.
            </p>
          </CardHeader>
          <CardContent>
            {isOwnPage && (
              <p
                role="status"
                className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
              >
                This is your contact page as visitors see it. To test it, send a
                message from another account.
              </p>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  {...register("subject")}
                  placeholder="What would you like to discuss?"
                  disabled={sendMessageMutation.isPending}
                  className="mt-1"
                />
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.subject.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="content">Message</Label>
                <Textarea
                  id="content"
                  {...register("content")}
                  placeholder="Tell them about your goals, what you're looking for in a coach, or any specific questions you have..."
                  rows={6}
                  disabled={sendMessageMutation.isPending}
                  className="mt-1"
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.content.message}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Be specific about your goals and what you're hoping to achieve
                  with coaching.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Link to="/$username" params={{ username: coach.username }}>
                  <Button type="button">Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  disabled={sendMessageMutation.isPending || isOwnPage}
                  variant="primary"
                  className="min-w-[120px]"
                >
                  {sendMessageMutation.isPending ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {isSignedIn ? "Send Message" : "Sign Up & Send"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 rounded-lg bg-blue-50 p-4">
          <h3 className="font-medium text-blue-900">What happens next?</h3>
          <ul className="mt-2 space-y-1 text-sm text-blue-800">
            <li>
              • {coach.firstName} will receive an email notification about your
              message
            </li>
            <li>
              • You'll be redirected to your messages where you can continue the
              conversation
            </li>
            <li>• You'll get an email when {coach.firstName} replies</li>
            <li>
              • All your conversations are saved in your messages dashboard
            </li>
          </ul>
        </div>

        {/* Auth Modal */}
        <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
          <DialogContent className="w-full max-w-lg p-0">
            <AuthModal isModalView={true} onSuccess={handleAuthSuccess} />
          </DialogContent>
        </Dialog>
      </div>
    </CoachSiteChrome>
  );
}
